/* eslint-disable react/prop-types */
import { navigate } from 'gatsby';
import React, { createContext, useEffect, useState } from 'react';

import { ProfessionalProfileService } from '../services/profissional/professional';

/**
 * Contexto de login que fornece informações de autenticação e gerenciamento de sessão.
 */
export const LoginContext = createContext();

/**
 * Define as rotas permitidas para acesso.
 */
const allowedRoutes = {
	'/saude/pre-cadastro/': true,
	'/saude/selecionar-estado/': true,
	'/saude/pre-cadastro-confirmacao/': true,
	'/saude/verificacao-inscricao/': true,
	'/404': true,
	'redefinicao-de-senha': true,
};

/**
 * Define as rotas relacionadas à diversidade.
 */
const diversityRoutes = {
	'/saude/diversidade/': true,
	'/saude/cadastro-diversidade-resumo/': true,
	'/saude/cadastro-diversidade/': true,
};

/**
 * Define as rotas não permitidas quando o perfil está sob análise.
 */
const underAnalysisNotAllowedRoutes = {
	'/saude/cadastro-atendimento-confirmacao/': true,
	'/saude/cadastro-atendimento/': true,
	'/saude/cadastro-concluido/': true,
	'/saude/cadastro-dados-atendimento/': true,
	'/saude/cadastro-dados-pessoais/': true,
	'/saude/painel-inicial/': true,
};

/**
 * Define as rotas permitidas quando a conta não está ativa.
 */
const notActiveAccountAllowedRoutes = {
	'/seguranca/minha-conta/': true,
	'/seguranca/confirmacao-reativar-conta/': true,
	'/seguranca/reativar-conta/': true,
};

/**
 * Verifica se a rota atual é permitida.
 * @returns true se a rota é permitida, senão false.
 */
const isAllowedRoute = () => allowedRoutes[window.location.pathname] || allowedRoutes[window.location.pathname.split('/')[1]];

/**
 * Verifica se a rota atual é relacionada à diversidade.
 * @returns true se a rota é de diversidade, senão false.
 */
const isDiversityRoute = () => {
	const route = window.location.pathname;
	const formattedRoute = route.endsWith('/') ? route : `${route}/`;

	return diversityRoutes[formattedRoute];
};

/**
 * Verifica se a rota atual não é permitida quando o perfil está sob análise.
 * @returns true se a rota não é permitida, senão false.
 */
const isUnderAnalysisNotAllowedRoute = () => underAnalysisNotAllowedRoutes[window.location.pathname];

/**
 * Verifica se a rota atual é permitida quando a conta não está ativa.
 * @returns true se a rota é permitida, senão false.
 */
const isNotActiveAccountAllowedRoute = () => notActiveAccountAllowedRoutes[window.location.pathname];

/**
 * Manipula o status de aprovação e redireciona o usuário para páginas apropriadas com base em diferentes condições.
 * @param status O status de aprovação do usuário.
 * @param current_step O passo atual na verificação do perfil.
 * @param postRegistrationUnderAnalysis Indica se o perfil está sob análise pós-registro.
 * @param published Indica se o perfil está publicado.
 */
function handleApprovalStatus(status, current_step, postRegistrationUnderAnalysis, published) {
	if (status !== 'approved') {
		return;
	}
	switch (true) {
		case current_step === 'board_registration_number':
			navigate('/saude/verificacao-inscricao/');
			break;
		case current_step === 'add_intersectionality_data' && !isDiversityRoute():
			navigate('/saude/diversidade/');
			break;
		case current_step && postRegistrationUnderAnalysis && isUnderAnalysisNotAllowedRoute():
		case !current_step && !published && !isNotActiveAccountAllowedRoute():
			navigate('/saude/painel-inicial/');
			break;
		default:
	}
}

/**
 * Provedor de contexto para o contexto de login.
 * @param {Object} props As propriedades do componente LoginContextProvider
 * @param {React.ReactNode} props.children Componentes filhos para os quais o contexto de login é fornecido.
 */
export function LoginContextProvider({ children }) {
	const [professionalData, setProfessionalData] = useState({});

	/**
	 * Obtém o status profissional e atualiza o estado do contexto de login.
	 */
	async function getStatusProfessional() {
		try {
			const getProfessionalStatus = new ProfessionalProfileService();
			const getProfessionalData = await getProfessionalStatus.getFullProfessionalProfile();
			const { professional, email } = getProfessionalData.data;

			const { profile_status: status, published, active, verification_steps } = professional;

			const { current_step, steps } = verification_steps;

			const postRegistrationUnderAnalysis = steps.post_registration_data.is_submitted;

			setProfessionalData({
				status,
				published,
				active,
				email,
				verificationSteps: verification_steps,
			});

			handleApprovalStatus(status, current_step, postRegistrationUnderAnalysis, published);
		} catch (error) {
			if (!isAllowedRoute()) navigate('/');
		}
	}

	useEffect(() => {
		if (sessionStorage.getItem('authToken')) {
			getStatusProfessional();
		}
	}, []);

	return (
		<LoginContext.Provider value={{ professionalData, setProfessionalData, getStatusProfessional }}>{children}</LoginContext.Provider>
	);
}
